import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Button, Form, FormGroup, Label } from 'reactstrap';
import { Field, reduxForm, formValueSelector } from 'redux-form';
import { isRequired, validateEndDate } from '../../../../../utils/validators';
import {
  PREVENTIVE_TASK_EXECUTION_STATUS,
  PREVENTIVE_TASK_STATUS_ACTIVITY,
} from '../../../../../config/constants';
import FormItem from '../../../../common/forms/FormItem';
import { DATE_TIME_FORMAT } from '../../../../../config/locale';
import TextInput from '../../../../common/forms/input/TextInput';
import FileInput from '../../../../common/forms/input/FileInput';
import Select from '../../../../common/forms/select/Select';
import { enumToSelectOptions } from '../../../../../utils/enum';
import Modal from '../../../../common/modal/Modal';
import FormFooter from '../../../../common/forms/FormFooter';

const FORM_NAME = 'PreventiveTaskProjectionExecutionModalForm';
const RESOURCE_PAGE_SIZE = 8;

const getSelectValue = (field) => {
  if (field && typeof field === 'object') return field.value;
  return field;
};

const toNumber = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  return Number(value);
};

const validateCompletedDate = (completedDate, allValues) =>
  validateEndDate(completedDate, allValues.startedDate);

const validateSpentAmount = (value) => {
  if (value === undefined || value === null || value === '') return undefined;

  const amount = Number(String(value).replace(',', '.'));

  if (Number.isNaN(amount)) return 'Ingrese un monto válido';
  if (amount < 0) return 'El monto no puede ser negativo';

  return undefined;
};

const normalizeText = (value) =>
  String(value || '')
    .trim()
    .toUpperCase();

const isAlreadyPostponed = (projectionItem) =>
  normalizeText(projectionItem && projectionItem.alertStatus) === 'POSTPONE';

const isResourceGroup = (projectionItem) =>
  normalizeText(projectionItem && projectionItem.rowType) === 'RESOURCE_GROUP';

const getExecutionStatusOptions = (projectionItem) => {
  const options = enumToSelectOptions(PREVENTIVE_TASK_EXECUTION_STATUS);

  if (!isAlreadyPostponed(projectionItem)) {
    return options;
  }

  return options.filter(
    (option) =>
      String(option.value) !==
      String(PREVENTIVE_TASK_EXECUTION_STATUS.POSTPONE.value),
  );
};

const getResourceId = (resource) => {
  if (!resource) return null;

  if (resource.resourceId !== null && resource.resourceId !== undefined) {
    return resource.resourceId;
  }

  if (
    resource.companyResource &&
    resource.companyResource.id !== null &&
    resource.companyResource.id !== undefined
  ) {
    return resource.companyResource.id;
  }

  if (
    resource.companyResourceId !== null &&
    resource.companyResourceId !== undefined
  ) {
    return resource.companyResourceId;
  }

  if (
    resource.preventiveTaskResourceId !== null &&
    resource.preventiveTaskResourceId !== undefined
  ) {
    return resource.preventiveTaskResourceId;
  }

  if (resource.id !== null && resource.id !== undefined) {
    return resource.id;
  }

  return null;
};

const getResourceName = (resource) => {
  if (!resource) return '-';

  if (resource.resourceName) {
    return resource.resourceName;
  }

  if (resource.companyResource && resource.companyResource.name) {
    return resource.companyResource.name;
  }

  if (resource.name) {
    return resource.name;
  }

  return '-';
};

const getValidResourceIds = (resources) =>
  resources
    .map((resource) => getResourceId(resource))
    .filter((resourceId) => resourceId !== null && resourceId !== undefined)
    .map(String);

const ResourceCheckboxGroup = ({ input, resources }) => {
  const [page, setPage] = useState(0);
  const [searchText, setSearchText] = useState('');

  const selectedValues = Array.isArray(input.value)
    ? input.value.map(String)
    : [];

  const filteredResources = useMemo(() => {
    const normalizedSearch = normalizeText(searchText);

    if (!normalizedSearch) {
      return resources;
    }

    return resources.filter((resource) =>
      normalizeText(getResourceName(resource)).includes(normalizedSearch),
    );
  }, [resources, searchText]);

  const totalPages = Math.ceil(filteredResources.length / RESOURCE_PAGE_SIZE);

  const safePage = totalPages > 0 && page >= totalPages ? totalPages - 1 : page;

  const startIndex = safePage * RESOURCE_PAGE_SIZE;
  const endIndex = startIndex + RESOURCE_PAGE_SIZE;

  const visibleResources = filteredResources.slice(startIndex, endIndex);

  const toggleResource = (resourceId) => {
    const value = String(resourceId);

    if (selectedValues.includes(value)) {
      input.onChange(selectedValues.filter((item) => item !== value));
      return;
    }

    input.onChange([...selectedValues, value]);
  };

  const selectAll = () => {
    input.onChange(getValidResourceIds(resources));
  };

  const clearAll = () => {
    input.onChange([]);
  };

  const selectVisible = () => {
    const visibleIds = getValidResourceIds(visibleResources);

    input.onChange(Array.from(new Set([...selectedValues, ...visibleIds])));
  };

  const clearVisible = () => {
    const visibleIds = getValidResourceIds(visibleResources);

    input.onChange(
      selectedValues.filter((value) => !visibleIds.includes(String(value))),
    );
  };

  const goPreviousPage = () => {
    if (safePage > 0) {
      setPage(safePage - 1);
    }
  };

  const goNextPage = () => {
    if (safePage + 1 < totalPages) {
      setPage(safePage + 1);
    }
  };

  const onChangeSearchText = (event) => {
    setSearchText(event.target.value);
    setPage(0);
  };

  if (!resources.length) {
    return (
      <div className="text-muted">No hay recursos asociados a esta tarea.</div>
    );
  }

  return (
    <div>
      <div className="mb-2">
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="Buscar recurso..."
          value={searchText}
          onChange={onChangeSearchText}
        />
      </div>

      <div className="mb-2 d-flex flex-wrap">
        <Button
          type="button"
          color="link"
          size="sm"
          className="p-0 mr-3"
          onClick={selectVisible}
        >
          Seleccionar visibles
        </Button>

        <Button
          type="button"
          color="link"
          size="sm"
          className="p-0 mr-3"
          onClick={clearVisible}
        >
          Limpiar visibles
        </Button>

        <Button
          type="button"
          color="link"
          size="sm"
          className="p-0 mr-3"
          onClick={selectAll}
        >
          Seleccionar todos
        </Button>

        <Button
          type="button"
          color="link"
          size="sm"
          className="p-0"
          onClick={clearAll}
        >
          Limpiar todos
        </Button>
      </div>

      <div
        style={{
          maxHeight: 260,
          overflowY: 'auto',
          border: '1px solid #e5e5e5',
          borderRadius: 4,
          padding: 8,
        }}
      >
        {!visibleResources.length ? (
          <div className="text-muted">No se encontraron recursos.</div>
        ) : (
          visibleResources.map((resource) => {
            const resourceId = getResourceId(resource);

            if (resourceId === null || resourceId === undefined) {
              return null;
            }

            return (
              <div key={resourceId} className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id={`completed-resource-${resourceId}`}
                  checked={selectedValues.includes(String(resourceId))}
                  onChange={() => toggleResource(resourceId)}
                />

                <Label
                  className="form-check-label"
                  htmlFor={`completed-resource-${resourceId}`}
                >
                  {getResourceName(resource)}
                </Label>
              </div>
            );
          })
        )}
      </div>

      <div className="d-flex justify-content-between align-items-center mt-2">
        <small className="text-muted">
          Mostrando {filteredResources.length ? startIndex + 1 : 0} -{' '}
          {Math.min(endIndex, filteredResources.length)} de{' '}
          {filteredResources.length} recursos. Seleccionados:{' '}
          {selectedValues.length}
        </small>

        <div>
          <Button
            type="button"
            color="secondary"
            size="sm"
            className="mr-2"
            onClick={goPreviousPage}
            disabled={safePage <= 0}
          >
            Anterior
          </Button>

          <Button
            type="button"
            color="secondary"
            size="sm"
            onClick={goNextPage}
            disabled={safePage + 1 >= totalPages}
          >
            Siguiente
          </Button>
        </div>
      </div>

      <small className="text-muted d-block mt-2">
        Los recursos seleccionados se registrarán como realizados. Los no
        seleccionados se postergarán.
      </small>
    </div>
  );
};

ResourceCheckboxGroup.propTypes = {
  input: PropTypes.shape({
    value: PropTypes.oneOfType([
      PropTypes.array,
      PropTypes.string,
      PropTypes.number,
    ]),
    onChange: PropTypes.func.isRequired,
  }).isRequired,
  resources: PropTypes.arrayOf(PropTypes.shape({})),
};

ResourceCheckboxGroup.defaultProps = {
  resources: [],
};

const PreventiveTaskProjectionExecutionModalBody = ({
  handleSubmit,
  loading,
  projectionItem,
  onCancel,
  status,
  taskResources,
}) => {
  if (!projectionItem) return null;

  const selectedStatus = getSelectValue(status);

  const alreadyPostponed = isAlreadyPostponed(projectionItem);
  const statusOptions = getExecutionStatusOptions(projectionItem);
  const resourceGroup = isResourceGroup(projectionItem);

  const isExtended =
    selectedStatus === PREVENTIVE_TASK_EXECUTION_STATUS.EXTENDED.value ||
    selectedStatus === PREVENTIVE_TASK_EXECUTION_STATUS.POSTPONE.value;

  const showResourceSelection = taskResources.length > 0;

  const alertStatus =
    PREVENTIVE_TASK_STATUS_ACTIVITY[projectionItem.alertStatus];

  return (
    <Form onSubmit={handleSubmit}>
      <div className="mb-3">
        <strong>{projectionItem.preventiveTaskName}</strong>

        <div className="text-muted">
          Código: {projectionItem.companyPreventiveTaskId || '-'}
        </div>

        {projectionItem.resourceName && (
          <div className="text-muted">
            Recurso: {projectionItem.resourceName}
          </div>
        )}

        {resourceGroup && taskResources.length > 0 && (
          <div className="text-muted">
            Recursos pendientes: {taskResources.length}
          </div>
        )}

        {alertStatus && (
          <div className="text-muted">Estado actual: {alertStatus.label}</div>
        )}

        {alreadyPostponed && (
          <small className="text-muted d-block mt-1">
            Esta ejecución ya está postergada. No puede postergarse nuevamente.
          </small>
        )}
      </div>

      <FormGroup row>
        <FormItem label="Fecha Inicio" required>
          <Field
            name="startedDate"
            component={TextInput}
            type="datetime-local"
            placeholder={DATE_TIME_FORMAT}
            validate={[isRequired]}
          />
        </FormItem>
      </FormGroup>

      <FormGroup row>
        <FormItem label="Fecha Culminación" required>
          <Field
            name="completedDate"
            component={TextInput}
            type="datetime-local"
            placeholder={DATE_TIME_FORMAT}
            validate={[isRequired, validateCompletedDate]}
          />
        </FormItem>
      </FormGroup>

      <FormGroup row>
        <FormItem label="Estado" required>
          <Field
            name="status"
            component={Select}
            options={statusOptions}
            placeholder="Seleccione estado"
            validate={[isRequired]}
            isClearable
          />
        </FormItem>
      </FormGroup>

      {showResourceSelection && (
        <FormGroup row>
          <FormItem label="Recursos realizados">
            <Field
              name="completedResourceIds"
              component={ResourceCheckboxGroup}
              resources={taskResources}
            />
          </FormItem>
        </FormGroup>
      )}

      {isExtended && (
        <FormGroup row>
          <FormItem label="Días a Extender" required>
            <Field
              name="extensionDays"
              component={TextInput}
              type="number"
              placeholder="Ingrese cantidad de días"
              normalize={toNumber}
              validate={[isRequired]}
            />
          </FormItem>
        </FormGroup>
      )}

      <FormGroup row>
        <FormItem label="Archivo Adjunto">
          <Field
            name="attachedFile"
            component={FileInput}
            accept=".pdf,.jpg,.jpeg,.png"
            multiple={false}
          />
        </FormItem>
      </FormGroup>

      <FormGroup row>
        <FormItem label="Monto Gastado">
          <Field
            name="spentAmount"
            component={TextInput}
            type="number"
            min="0"
            step="0.01"
            placeholder="0.00"
            normalize={toNumber}
            validate={[validateSpentAmount]}
          />
        </FormItem>
      </FormGroup>

      <FormGroup row>
        <FormItem label="Comentario">
          <Field
            name="comment"
            component={TextInput}
            maxLength={500}
            type="textarea"
            placeholder="Comentario de la ejecución"
          />
        </FormItem>
      </FormGroup>

      <FormGroup row>
        <FormItem label="Observación">
          <Field
            name="observation"
            component={TextInput}
            maxLength={500}
            type="textarea"
            placeholder="Observación de la ejecución"
          />
        </FormItem>
      </FormGroup>

      <div className="d-flex justify-content-end mt-3">
        <Button
          type="button"
          color="secondary"
          className="mr-2"
          onClick={onCancel}
          disabled={loading}
        >
          Cancelar
        </Button>

        <FormFooter
          saveButtonDisabled={loading}
          saveButtonIcon={loading ? 'fa fa-spinner fa-spin' : 'fa fa-save'}
        />
      </div>
    </Form>
  );
};

PreventiveTaskProjectionExecutionModalBody.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  projectionItem: PropTypes.shape({
    rowType: PropTypes.string,
    preventiveTaskName: PropTypes.string,
    companyPreventiveTaskId: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    resourceName: PropTypes.string,
    alertStatus: PropTypes.string,
  }),
  onCancel: PropTypes.func.isRequired,
  status: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
  taskResources: PropTypes.arrayOf(PropTypes.shape({})),
};

PreventiveTaskProjectionExecutionModalBody.defaultProps = {
  loading: false,
  projectionItem: null,
  status: null,
  taskResources: [],
};

const ReduxPreventiveTaskProjectionExecutionModalBodyForm = reduxForm({
  form: FORM_NAME,
  enableReinitialize: true,
})(PreventiveTaskProjectionExecutionModalBody);

const selector = formValueSelector(FORM_NAME);

const ReduxPreventiveTaskProjectionExecutionModalBody = connect((state) => ({
  status: selector(state, 'status'),
}))(ReduxPreventiveTaskProjectionExecutionModalBodyForm);

const PreventiveTaskProjectionExecutionModal = ({
  show,
  onClose,
  onSubmit,
  loading,
  projectionItem,
  initialValues,
  taskResources,
}) => (
  <Modal
    show={show}
    title="Registrar Ejecución Programada"
    body={
      <ReduxPreventiveTaskProjectionExecutionModalBody
        projectionItem={projectionItem}
        initialValues={initialValues}
        loading={loading}
        onCancel={onClose}
        onSubmit={onSubmit}
        taskResources={taskResources}
      />
    }
    onClickClose={onClose}
    size="lg"
  />
);

PreventiveTaskProjectionExecutionModal.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  projectionItem: PropTypes.shape({}),
  initialValues: PropTypes.shape({}),
  taskResources: PropTypes.arrayOf(PropTypes.shape({})),
};

PreventiveTaskProjectionExecutionModal.defaultProps = {
  show: false,
  loading: false,
  projectionItem: null,
  initialValues: null,
  taskResources: [],
};

export default PreventiveTaskProjectionExecutionModal;
